import { useState, useEffect, useCallback } from "react";
import apiClient from "utils/apiClient";

const useSecurityQuestions = (page = 0, pageSize = 10) => {
  const authToken = sessionStorage.getItem("authToken");
  const [securityQns, setSecurityQns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const fetchSecurityQuestions = useCallback(async () => {
    if (!authToken) return;
    
    try {
      setLoading(true);
      setError(null);

      const baseUrl = "/identity-service/api/v1/security-questions";
      const response = await apiClient.get(
        `${baseUrl}?page=${page}&size=${pageSize}`
      );

      const { content = [], totalPages = 1, totalElements = 0 } = response.data;

      setSecurityQns(content);
      setTotalPages(totalPages);
      setTotalElements(totalElements);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load security questions";
      setError(errorMessage);
      setSecurityQns([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [authToken, page, pageSize]);

  // Add a new security question
  const addSecurityQuestion = useCallback(
    async (questionData) => {
      try {
        const response = await apiClient.post(
          "/identity-service/api/v1/security-questions",
          questionData
        );
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchSecurityQuestions();
        return response.data;
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        setError(message);
        throw new Error(message);
      }
    },
    [fetchSecurityQuestions]
  );

  // Update a security question by ID
  const updateSecurityQuestion = useCallback(
    async (id, questionData) => {
      try {
        const response = await apiClient.patch(
          `/identity-service/api/v1/security-questions/${id}`,
          questionData
        );
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchSecurityQuestions();
        return response.data;
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        setError(message);
        throw new Error(message);
      }
    },
    [fetchSecurityQuestions]
  );

  // Delete a security question by ID
  const deleteSecurityQuestion = useCallback(
    async (id) => {
      try {
        await apiClient.delete(`/identity-service/api/v1/security-questions/${id}`);
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchSecurityQuestions();
      } catch (err) {
        const message = err.response?.data?.message || "Delete failed";
        setError(message);
        throw err;
      }
    },
    [fetchSecurityQuestions]
  );

  // Auto-fetch when page or pageSize changes
  useEffect(() => {
    if (authToken) {
      fetchSecurityQuestions();
    }
  }, [authToken, fetchSecurityQuestions]);

  return {
    securityQns,
    loading,
    error,
    totalPages,
    totalElements,
    fetchSecurityQuestions,
    addSecurityQuestion,
    updateSecurityQuestion,
    deleteSecurityQuestion,
  };
};

export default useSecurityQuestions;
